import { Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ProjectUserRole } from '@maxus/common'

import { Avatar, Button, Separator } from '../../base'
import { ProjectRoleSelect } from '../project-role-select'
import { UserCard } from '../user-card'
import { MemberSearch, type MemberSearchResult } from './member-search'

type MemberCallback = {
  id: string
  role: ProjectUserRole
}

type MemberPanelProps = {
  onClose: () => void
  onConfirm: (props: { added: MemberCallback[]; updated: MemberCallback[]; removed: MemberCallback[] }) => void
  existingMembers: { id: string; name: string; avatar: string; desc: string; role: ProjectUserRole }[]
}

export const MemberPanel = ({ onClose, onConfirm, existingMembers }: MemberPanelProps) => {
  const { t } = useTranslation()

  const [draftMembers, setDraftMembers] = useState(existingMembers)

  const updateMemberRole = (memberId: string, role: ProjectUserRole) => {
    setDraftMembers(current => current.map(member => (member.id === memberId ? { ...member, role } : member)))
  }
  const removeMember = (memberId: string) => {
    setDraftMembers(current => current.filter(member => member.id !== memberId))
  }
  const addMember = (member: MemberSearchResult) => {
    setDraftMembers(current => {
      if (current.some(draftMember => draftMember.id === member.id)) return current
      return [
        ...current,
        {
          id: member.id,
          name: member.name,
          avatar: member.avatarUrl ?? '',
          desc: member.department || member.email || '',
          role: 'member',
        },
      ]
    })
  }
  const memberSearchValues = draftMembers.map(member => ({
    id: member.id,
    unselectable: !existingMembers.some(existingMember => existingMember.id === member.id),
  }))

  const calculateChanges = () => {
    const added = draftMembers.filter(draft => !existingMembers.some(existing => existing.id === draft.id))
    const updated = draftMembers.filter(draft => {
      const existing = existingMembers.find(existing => existing.id === draft.id)
      return existing && existing.role !== draft.role
    })
    const removed = existingMembers.filter(existing => !draftMembers.some(draft => draft.id === existing.id))

    return { added, updated, removed }
  }

  return (
    <div className="box-border flex h-full w-full flex-col rounded-l-panel border-l border-border-subtle bg-surface-panel px-6 py-3">
      {/* header */}
      <div className="grow-0 shrink-0 w-full flex items-start justify-between">
        {/* desc */}
        <div className="flex flex-col gap-1">
          <span className="text-base font-semibold leading-6 text-text-primary">{t('Manage project members')}</span>
          <span className="text-xs leading-[18px] text-text-secondary">
            {t('View current members, and continue adding members or managing permissions.')}
          </span>
        </div>

        <Button aria-label={t('Close panel')} onClick={onClose} size="icon-sm" variant="ghost">
          <X className="size-5" />
        </Button>
      </div>

      <Separator className="mt-1 mb-5" />

      {/* search */}
      <div className="grow-0 shrink-0 w-full">
        <span className="text-base font-semibold leading-6 text-text-primary">{t('Add member')}</span>
        <MemberSearch onSelect={addMember} onUnSelect={removeMember} values={memberSearchValues} />
      </div>

      <Separator className="my-4" />

      {/* candidates */}
      <div className="grow shrink min-h-0 flex flex-col gap-3 overflow-y-auto overflow-x-hidden">
        {draftMembers.map(existingMember => {
          return (
            <div
              key={existingMember.id}
              className="w-full flex h-14 items-center justify-between gap-2 rounded-lg px-2.5 transition-colors hover:bg-surface-card"
            >
              {/* left */}
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <UserCard className="hover:no-underline" userId={existingMember.id}>
                  {/* avatar */}
                  <Avatar avatarUrl={existingMember.avatar} name={existingMember.name} />

                  {/* desc */}
                  <span className="ml-3 flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold leading-[22px] text-text-primary">
                      {existingMember.name}
                    </span>
                    <span className="truncate text-xs leading-[18px] text-text-tertiary">{existingMember.desc}</span>
                  </span>
                </UserCard>
              </div>

              {/* right */}
              <div className="flex shrink-0 items-center gap-1">
                {/* role selector */}
                <ProjectRoleSelect
                  ariaLabel={t('{{name}} project role', { name: existingMember.name })}
                  onValueChange={value => updateMemberRole(existingMember.id, value)}
                  value={existingMember.role}
                />

                {/* remove button */}
                <Button
                  aria-label={t('Remove member')}
                  className="text-status-danger hover:text-status-danger/80"
                  // disabled={rowDisabled}
                  onClick={() => removeMember(existingMember.id)}
                  size="icon-sm"
                  variant="ghost"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <Separator className="mt-5 mb-2.5" />

      {/* footer */}
      <div className="grow-0 shrink-0 w-full flex justify-end">
        {/* save button */}
        <Button
          className="h-auto rounded-md px-7.5 py-2.5 text-sm font-semibold"
          onClick={() => onConfirm(calculateChanges())}
        >
          {t('Confirm')}
        </Button>
      </div>
    </div>
  )
}
